import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Progress, Button, Space, Typography, Table, Tag, Modal, Collapse,
  Statistic, Row, Col, Badge, Alert
} from 'antd';
import {
  StopOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  SyncOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';

const { Text, Title } = Typography;
const { Panel } = Collapse;

/**
 * BulkProgressPanel Component
 *
 * Toplu işlem ilerleme durumunu gösterir.
 * WebSocket üzerinden real-time güncelleme alır.
 * İptal butonu ve detaylı sonuç tablosu içerir.
 */
const BulkProgressPanel = ({
  batchId,
  onCancel,
  onComplete,
  wsConnection // WebSocket connection for real-time updates
}) => {
  const [batchState, setBatchState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Fetch initial batch status
  useEffect(() => {
    if (batchId) {
      fetchBatchStatus();
    }
  }, [batchId]);

  // WebSocket listener for batch updates
  useEffect(() => {
    if (!wsConnection) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.batch_id !== batchId) return;

        switch (data.type) {
          case 'batch_progress':
            setBatchState(data.data);
            break;
          case 'batch_completed':
            setBatchState({ ...data.data, status: 'completed' });
            if (onComplete) onComplete(data.data);
            break;
          case 'batch_cancelled':
            setBatchState({ ...data.data, status: 'cancelled' });
            break;
          case 'batch_error':
            setBatchState({ ...data.data, status: 'failed', error: data.data.error });
            break;
          default:
            break;
        }
      } catch (e) {
        logger.error('WebSocket message parse error:', e);
      }
    };

    wsConnection.addEventListener('message', handleMessage);
    return () => wsConnection.removeEventListener('message', handleMessage);
  }, [wsConnection, batchId, onComplete]);

  // Polling fallback if WebSocket is not available
  useEffect(() => {
    if (wsConnection || !batchId) return;

    const interval = setInterval(() => {
      fetchBatchStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [batchId, wsConnection]);

  const fetchBatchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/mqtt/batch/${batchId}/status`);
      setBatchState(response.data);

      if (response.data.status === 'completed' || response.data.status === 'cancelled') {
        if (onComplete) onComplete(response.data);
      }
    } catch (error) {
      logger.error('Batch status fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    Modal.confirm({
      title: 'Batch İşlemini İptal Et',
      icon: <ExclamationCircleOutlined />,
      content: 'Devam eden işlem iptal edilecek. Zaten gönderilmiş komutlar geri alınamaz. Devam etmek istiyor musunuz?',
      okText: 'İptal Et',
      okType: 'danger',
      cancelText: 'Vazgeç',
      onOk: async () => {
        try {
          setCancelling(true);
          await axios.delete(`/api/v1/mqtt/batch/${batchId}/cancel`);
          if (onCancel) onCancel();
        } catch (error) {
          logger.error('Batch cancel error:', error);
        } finally {
          setCancelling(false);
        }
      }
    });
  };

  if (!batchState) {
    return (
      <Card loading={loading}>
        <Text type="secondary">Batch durumu yükleniyor...</Text>
      </Card>
    );
  }

  const isRunning = batchState.status === 'running';
  const isCompleted = batchState.status === 'completed';
  const isCancelled = batchState.status === 'cancelled';
  const isFailed = batchState.status === 'failed';

  const progressStatus = isCompleted ? 'success' : isCancelled || isFailed ? 'exception' : 'active';

  const resultColumns = [
    {
      title: 'Cihaz',
      dataIndex: 'device_name',
      key: 'device_name',
      render: (name, record) => (
        <Space>
          {record.success ? (
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          )}
          <Text>{name || `Device ${record.device_id}`}</Text>
        </Space>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'success',
      key: 'success',
      render: (success) => (
        <Tag color={success ? 'green' : 'red'}>
          {success ? 'Başarılı' : 'Başarısız'}
        </Tag>
      ),
    },
    {
      title: 'Komut ID',
      dataIndex: 'command_id',
      key: 'command_id',
      render: (id) => id ? <Text code style={{ fontSize: '10px' }}>{id.substring(0, 8)}...</Text> : '-',
    },
    {
      title: 'Hata',
      dataIndex: 'error',
      key: 'error',
      render: (error) => error ? <Text type="danger" style={{ fontSize: '11px' }}>{error}</Text> : '-',
    },
  ];

  return (
    <Card
      title={
        <Space>
          {isRunning && <SyncOutlined spin />}
          {isCompleted && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
          {isCancelled && <StopOutlined style={{ color: '#faad14' }} />}
          {isFailed && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          <span>Toplu İşlem</span>
          <Tag color={isRunning ? 'processing' : isCompleted ? 'success' : 'warning'}>
            {batchState.command_type}/{batchState.command}
          </Tag>
        </Space>
      }
      extra={
        isRunning && (
          <Button
            danger
            icon={<StopOutlined />}
            onClick={handleCancel}
            loading={cancelling}
          >
            İptal Et
          </Button>
        )
      }
    >
      {/* Progress Bar */}
      <Progress
        percent={batchState.progress_percent || 0}
        status={progressStatus}
        strokeColor={{
          '0%': '#108ee9',
          '100%': '#87d068',
        }}
        format={(percent) => `${Math.round(percent)}%`}
      />

      {/* Statistics */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={6}>
          <Statistic
            title="Toplam"
            value={batchState.total}
            prefix={<Badge status="default" />}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Başarılı"
            value={batchState.completed_count}
            valueStyle={{ color: '#3f8600' }}
            prefix={<CheckCircleOutlined />}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Başarısız"
            value={batchState.failed_count}
            valueStyle={{ color: '#cf1322' }}
            prefix={<CloseCircleOutlined />}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Bekliyor"
            value={batchState.pending_count}
            valueStyle={{ color: '#faad14' }}
            prefix={<ClockCircleOutlined />}
          />
        </Col>
      </Row>

      {/* Status Message */}
      {batchState.error && (
        <Alert
          type="error"
          message="İşlem Hatası"
          description={batchState.error}
          style={{ marginTop: 16 }}
        />
      )}

      {isCancelled && (
        <Alert
          type="warning"
          message="İşlem İptal Edildi"
          description={`${batchState.completed_count + batchState.failed_count} / ${batchState.total} cihaz işlendi.`}
          style={{ marginTop: 16 }}
        />
      )}

      {/* Details Collapse */}
      {batchState.results && batchState.results.length > 0 && (
        <Collapse style={{ marginTop: 16 }}>
          <Panel
            header={
              <Space>
                <Text>Detaylı Sonuçlar</Text>
                <Badge count={batchState.results.length} style={{ backgroundColor: '#1890ff' }} />
              </Space>
            }
            key="results"
          >
            <Table
              dataSource={batchState.results}
              columns={resultColumns}
              rowKey={(record) => record.device_id || record.command_id}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ y: 300 }}
            />
          </Panel>
        </Collapse>
      )}

      {/* Timing Info */}
      <div style={{ marginTop: 16 }}>
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Başlangıç: {batchState.started_at ? new Date(batchState.started_at).toLocaleString('tr-TR') : '-'}
          </Text>
          {batchState.completed_at && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Bitiş: {new Date(batchState.completed_at).toLocaleString('tr-TR')}
            </Text>
          )}
        </Space>
      </div>
    </Card>
  );
};

export default BulkProgressPanel;
